import React, { useState, createContext, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { env } from 'constants/env';
import { LogToService } from 'V2/Utilities/Logger';
import { initApplication } from 'V2/Utilities/InitApplication';
import { agentAuth } from 'V2/Utilities/AgentAuth';
import {
  clearApplySession,
  persistApplySession,
  readApplySessionParams,
} from './applySession';
import { contextDefaults } from 'V2/Step';
import { ApplyContextType, AppType } from './types';
import { summaryDefaults } from './Defaults';
import { removeSessionValuesFromLocalStorage } from 'V2/Utilities/StorageHelpers';
import { useFullstory } from 'V2/Hooks/useFullstory';
import { formatAttributionData } from 'V2/Analytics';

type ApplyProviderProps = {
  children:
    | React.ReactNode
    | ((context: { policyState: string | null }) => React.ReactNode);
};

export const ApplyContext = createContext<ApplyContextType>({
  app: {
    appId: null,
    status: contextDefaults.status,
    config: contextDefaults.config,
    fields: [],
    summary: summaryDefaults,
  },
  setIsAnimating: () => {},
  isAnimating: false,
  setApp: () => {},
  resetApp: () => {},
  isReady: false,
});

export const useApplyProvider = () =>
  useContext<ApplyContextType>(ApplyContext);

export const ApplyProvider: React.FC<ApplyProviderProps> = ({ children }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [app, setApp] = useState<AppType>({
    appId: null,
    status: contextDefaults.status,
    config: contextDefaults.config,
    fields: [],
    summary: summaryDefaults,
  });

  const resetApp = () => {
    setApp({
      ...app,
      status: contextDefaults.status,
      config: contextDefaults.config,
      fields: [],
      summary: summaryDefaults,
    });
  };

  const history = useHistory();
  const { fsCommands } = useFullstory();

  // application bootstrap and attribution logic;
  // get attribution_link/data, set analytics;
  useEffect(() => {
    if (
      history.location?.pathname === '/search' ||
      history.location?.pathname === '/documents' ||
      history.location?.pathname?.startsWith('/checkout/')
    )
      return;
    if (
      history.location?.pathname === '/quote' &&
      ['development', 'ephemeral', 'staging', 'uat', 'partner'].includes(
        env.clearcoverEnv
      )
    )
      return;

    const {
      appId: appIdParam,
      clickId: clickIdParam,
      applicationNotFound,
    } = readApplySessionParams(history.location?.search ?? '');

    // If these exist in localStorage remove them
    // Should only exist on /documents route
    removeSessionValuesFromLocalStorage(['consumerToken', 'appId', 'clickId']);

    if (applicationNotFound) {
      clearApplySession();
      history.push('/search');
      return;
    }

    const isAgent = history.location?.pathname === '/agent/apply';
    const agentAuthToken = localStorage.getItem('authToken');

    if (isAgent || agentAuthToken) {
      if (appIdParam && clickIdParam) {
        sessionStorage.setItem('appId', appIdParam);
        sessionStorage.setItem('clickId', clickIdParam);
        sessionStorage.removeItem('marketingId');
      }

      agentAuth()
        .then((token) => {
          localStorage.setItem('authToken', token);
          if (sessionStorage.getItem('appId')) {
            initApplication()
              .then((attributionData) => {
                const prefixedData = formatAttributionData(
                  attributionData,
                  'none'
                );
                fsCommands.updatePersistedFullstoryMetadata(prefixedData);

                if (appIdParam && clickIdParam) {
                  history.push({ search: '' });
                }

                setIsReady(true);
              })
              .catch((error) => {
                LogToService.captureException({
                  errorType: 'client',
                  message: error.message,
                  thread: 'agentAuth::initApplication',
                  error: new Error(error),
                });

                window.location.href = env.portalHost;
              });
          } else {
            window.location.href = env.portalHost;
          }
        })
        .catch((error) => {
          LogToService.captureException({
            errorType: 'client',
            message: error.message,
            thread: 'agentAuth',
            error: new Error(error),
          });

          window.location.href = env.portalHost;
        });

      return;
    } else {
      // attribution bridge: the applicant_auth cookie was set by the /attribution_link redirect;
      // only the application and click ids travel in the URL (DLC-1725)
      if (appIdParam && clickIdParam) {
        persistApplySession({ appId: appIdParam, clickId: clickIdParam });
      }

      if (sessionStorage.getItem('appId')) {
        initApplication()
          .then((attributionData) => {
            const prefixedData = formatAttributionData(attributionData, 'none');
            fsCommands.updatePersistedFullstoryMetadata(prefixedData);

            if (appIdParam && clickIdParam) {
              history.push({ search: '' });
            }

            setIsReady(true);
          })
          .catch((error) => {
            LogToService.captureException({
              errorType: 'server',
              message: error.message,
              thread: 'initApplication',
              error: new Error(error),
            });

            history.push('/search');
          });
      } else {
        history.push('/search');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    const { product_segment } = app?.config?.features_by_key || {};
    const { quote_number } = app.summary?.metadata || {};

    fsCommands.updatePersistedFullstoryMetadata({
      product_segment: product_segment || 'none',
      new_insurance_application_id: app?.appId || 'none',
      new_quote_number: quote_number || 'none',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.config?.features_by_key?.product_segment, app?.appId]);

  return (
    <ApplyContext.Provider
      value={{
        app,
        isAnimating,
        setIsAnimating,
        setApp,
        resetApp,
        isReady,
      }}
    >
      {typeof children === 'function'
        ? children({ policyState: app?.config?.policy_enforced_state || null })
        : children}
    </ApplyContext.Provider>
  );
};
